import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useQuery } from '@apollo/client'
import { groupBy } from 'lodash'
import { useParams } from 'react-router-dom'

import { OutletStackedMenuItemGroupTree } from '../menuItemGroupTreeType'
import { MenuGroup } from '../OutletMenu/MenuGroup/MenuGroup'
import { MenuItemSearchResults } from '../OutletMenu/MenuItemSearchResults/MenuItemSearchResults'
import { GetSingleOutletMenuDocument } from '../OutletMenu/queries/__generated__/GetSingleOutletMenu.graphql-interface'
import { useOutletPageContext } from '../OutletPageContext'

export const OutletStackedMenuView: React.FC = () => {
  const {
    showMenuSearchResults,
    setTreeForModalData,
    outletFulfilment: {
      outlet,
      data: { currentFulfilment },
    },
  } = useOutletPageContext()

  const { menuGroupId } = useParams<{ menuGroupId: string }>()

  const menuItemGroupId = menuGroupId?.split(':')[1]

  const processMenuData = useCallback((menuItemGroups: any[]) => {
    if (!menuItemGroups) return []

    const menuItemsWithoutAddonItems = menuItemGroups.filter(
      ({ addOnItemsMenu }) => !addOnItemsMenu
    )
    const { parentMenus = [], subMenus = [] } = groupBy(
      menuItemsWithoutAddonItems,
      menuItemGroup => (menuItemGroup.parentMenu ? 'subMenus' : 'parentMenus')
    )

    return parentMenus.map(parentMenu => {
      const subMenusForParentMenu = subMenus.filter(
        subMenu => subMenu.parentMenu?.id === parentMenu.id
      )
      return {
        parentMenu,
        subMenus: subMenusForParentMenu,
      }
    })
  }, [])

  const hasSetTreeDataRef = useRef<string | undefined>(undefined)

  const { data, previousData } = useQuery(
    GetSingleOutletMenuDocument,
    menuItemGroupId
      ? {
          variables: {
            menuItemGroupId: menuItemGroupId,
            outletId: outlet.id,
            narrowFulfilmentMethods: currentFulfilment.narrowType,
          },
        }
      : { skip: true }
  )

  const menuItemGroups = (data || previousData)?.singleMenuItemGroupForOutlet

  const menuItemGroupTree = useMemo<OutletStackedMenuItemGroupTree>(
    () => processMenuData(menuItemGroups || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [menuItemGroups, currentFulfilment.endOfPrep, currentFulfilment.narrowType]
  )

  useEffect(() => {
    if (
      menuItemGroupTree.length > 0 &&
      hasSetTreeDataRef.current !== menuItemGroupId
    ) {
      setTreeForModalData(menuItemGroupTree)
      hasSetTreeDataRef.current = menuItemGroupId
    }
  }, [menuItemGroupTree, setTreeForModalData, menuItemGroupId])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  if (showMenuSearchResults)
    return <MenuItemSearchResults outletId={outlet.id} />

  return (
    <>
      {menuItemGroupTree.map(outletMenuGroup =>
        // make sure there are menu items in either the parent menu or submenus
        outletMenuGroup.parentMenu.outletMenuItems.length > 0 ||
        (outletMenuGroup.subMenus[0] &&
          outletMenuGroup.subMenus[0].outletMenuItems.length > 0) ? (
          <MenuGroup
            key={outletMenuGroup.parentMenu.id}
            outletId={outlet.id}
            outletParentMenu={outletMenuGroup.parentMenu}
            outletSubMenus={outletMenuGroup.subMenus}
          />
        ) : null
      )}
    </>
  )
}
